import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, Users, ScrollText, Settings as Cog,
  FileText, CreditCard, LogOut, HardHat, Sun, Moon,
} from "lucide-react";

const NAV = [
  { to: "/owner",               label: "Home",          Icon: LayoutDashboard, testId: "owner-nav-home",      exact: true },
  { to: "/owner/users",         label: "Users",         Icon: Users,           testId: "owner-nav-users" },
  { to: "/owner/logs",          label: "Logs",          Icon: ScrollText,      testId: "owner-nav-logs" },
  { to: "/owner/settings",      label: "Settings",      Icon: Cog,             testId: "owner-nav-settings" },
  { to: "/owner/forms",         label: "Forms",         Icon: FileText,        testId: "owner-nav-forms" },
  { to: "/owner/subscriptions", label: "Subscriptions", Icon: CreditCard,      testId: "owner-nav-subs" },
];

export default function OwnerSidebar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const loc = useLocation();
  const navigate = useNavigate();

  const isActive = (item) =>
    item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <aside
      data-testid="owner-sidebar"
      className="hidden md:flex flex-col w-64 shrink-0 min-h-screen sticky top-0 border-r-2"
      style={{ backgroundColor: "#0B1E3F", borderRightColor: "#F9D71C" }}
    >
      {/* Brand */}
      <div className="px-5 py-5 flex items-center gap-3 border-b-2" style={{ borderBottomColor: "rgba(249,215,28,0.3)" }}>
        <span className="inline-flex items-center justify-center h-11 w-11 bg-safety-yellow text-black border-2 border-safety-yellow">
          <HardHat className="w-6 h-6" strokeWidth={3} />
        </span>
        <div className="leading-tight">
          <div className="font-heading text-xl text-white">
            KARM<span className="text-safety-yellow">.</span>SAFETY
          </div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: "#F9D71C" }}>
            Owner Console
          </div>
        </div>
      </div>

      {/* Owner identity chip */}
      <div className="px-5 py-4 border-b-2" style={{ borderBottomColor: "rgba(249,215,28,0.2)" }}>
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/60 mb-1">Signed in</div>
        <div className="text-sm font-bold text-white truncate" title={user?.email}>
          {user?.name || "Platform Owner"}
        </div>
        <div className="text-xs text-white/60 truncate">{user?.email}</div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3">
        {NAV.map((item) => {
          const active = isActive(item);
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={`group relative flex items-center gap-3 px-5 h-12 text-sm font-bold uppercase tracking-wider transition-all duration-150 border-l-4 ${
                active
                  ? "text-black"
                  : "text-white/80 hover:text-white border-transparent hover:border-safety-yellow/60"
              }`}
              style={
                active
                  ? { backgroundColor: "#F9D71C", borderLeftColor: "#F9D71C" }
                  : undefined
              }
            >
              <item.Icon className="w-4 h-4" strokeWidth={2.75} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Footer actions */}
      <div className="p-4 border-t-2 space-y-2" style={{ borderTopColor: "rgba(249,215,28,0.3)" }}>
        <button
          type="button"
          data-testid="owner-theme-toggle"
          onClick={toggle}
          className="w-full h-11 inline-flex items-center justify-center gap-2 border-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-safety-yellow hover:text-black transition-colors"
          style={{ borderColor: "#F9D71C" }}
        >
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          {theme === "dark" ? "Light" : "Dark"} mode
        </button>
        <button
          type="button"
          data-testid="owner-logout"
          onClick={handleLogout}
          className="w-full h-11 inline-flex items-center justify-center gap-2 bg-safety-yellow text-black border-2 border-safety-yellow text-xs font-bold uppercase tracking-wider hover:bg-safety-yellowHover transition-colors"
        >
          <LogOut className="w-4 h-4" strokeWidth={3} />
          Logout
        </button>
      </div>
    </aside>
  );
}

// Mobile top bar (visible only on small screens)
export function OwnerMobileBar() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  return (
    <div
      data-testid="owner-mobile-bar"
      className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b-2"
      style={{ backgroundColor: "#0B1E3F", borderBottomColor: "#F9D71C" }}
    >
      <Link to="/owner" className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center h-9 w-9 bg-safety-yellow text-black border-2 border-safety-yellow">
          <HardHat className="w-4 h-4" strokeWidth={3} />
        </span>
        <span className="font-heading text-base text-white">
          OWNER<span className="text-safety-yellow">.</span>CONSOLE
        </span>
      </Link>
      <button
        onClick={async () => { await logout(); navigate("/"); }}
        data-testid="owner-logout-mobile"
        className="h-9 px-3 bg-safety-yellow text-black border-2 border-safety-yellow text-xs font-bold uppercase tracking-wider"
      >
        Exit
      </button>
      {/* tiny scroll-bar of links */}
      <div className="absolute left-0 right-0 top-14 flex gap-1 overflow-x-auto px-2 py-2 border-b-2" style={{ backgroundColor: "#0B1E3F", borderBottomColor: "rgba(249,215,28,0.3)" }}>
        {NAV.map((it) => {
          const active = it.exact ? loc.pathname === it.to : loc.pathname.startsWith(it.to);
          return (
            <Link
              key={it.to}
              to={it.to}
              className={`whitespace-nowrap px-3 h-8 inline-flex items-center text-[10px] font-bold uppercase tracking-wider border-2 ${
                active
                  ? "bg-safety-yellow text-black border-safety-yellow"
                  : "text-white border-white/30"
              }`}
            >
              {it.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
